import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { PlusOutlined } from '@ant-design/icons';
import html2canvas from 'html2canvas';
import { Button, Card } from 'antd';
import * as Cesium from 'cesium';
import parse from 'html-react-parser';
import { useNavigate, useParams } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import './ExpandedMapView.scss';
import MapPortalService from '../../../services/MapPortal/mapportal.service';
import Spinner from '../../../shared/components/Spinner';
import CustomMapMenu from '../../../shared/components/Menu';
import { useLayerProperitesContext, defaultLayerPropertiesState } from '../../../context/LayerPropertiesContext';

// Sub-Components
import MapPortalToolbar from './components/MapPortalToolbar';
import MapSettingsModal from './components/MapSettingsModal';
import { GeneralTabRef } from '../MapSettings/GeneralTab';
import { useMapContext } from '../../../context/MapContext';
import { QueryKeys } from '../../../enums/queryKeys';
import MapPortalMap from '../MapPortalMap/MapPortalMap';
import LegendDrawer from '../Legend/LegendDrawer';
import { applySymbologyPreview, useSymbologyPreview, type SymbologyPreview } from '../../../hooks/useSymbologyPreview';
import { useLegendUI } from '../../../context/MapLegendContext';
import { useSketchLogic } from '../../../hooks/useSketchLogic';
import { onSocketEvent, subscribeRoom } from '../../../services/socket.service';

const S3_BASE = 'https://map-portal-attachments.s3.ca-central-1.amazonaws.com/';

interface ExpandedMapViewProps {
  expandedCard: string | null;
  isNewMap: boolean;
  isDataPortalModalVisible: boolean;
  folderId: string;
  setIsNewMap: React.Dispatch<React.SetStateAction<boolean>>;
  setIsDataPortalModalVisible: React.Dispatch<React.SetStateAction<boolean>>;
}

export interface ModalVisibility {
  settings: boolean;
  delete: boolean;
  password: boolean;
  group: boolean;
  thumbnail: boolean;
  newMap: boolean;
}

const ExpandedMapView: React.FC<ExpandedMapViewProps> = ({ expandedCard, isNewMap, setIsDataPortalModalVisible, isDataPortalModalVisible }) => {
  // ... (Hooks and State remain unchanged) ...
  const navigate = useNavigate();
  const { id } = useParams();
  const generalTabRef = useRef<GeneralTabRef>(null);
  const viewerRef = useRef<Cesium.Viewer | null>(null);
  const [isModalVisible, setIsModalVisible] = useState<ModalVisibility>({
    settings: false,
    delete: false,
    password: false,
    group: false,
    thumbnail: false,
    newMap: isNewMap, // Initialize based on prop
  });

  const { createMap, getMapById, getMapLayers, editMap, getLayerBounds } = MapPortalService();
  const mapIdNumber = expandedCard ? Number(expandedCard) : undefined;
  const { data: map, isLoading: mapLoading } = useQuery({
    ...getMapById(expandedCard || undefined),
    enabled: !!expandedCard && expandedCard !== 'new-map',
  });
  const {
    data: mapLayersResponse,
    isLoading: mapLayersLoading,
    refetch: refetchMapLayers,
  } = useQuery({
    ...getMapLayers(mapIdNumber),
    enabled: !isModalVisible.newMap && !!mapIdNumber && expandedCard !== 'new-map',
    // A freshly-uploaded layer flips "processing" → "ready" via the `layer:status`
    // socket event below. This slow interval is only a backstop for a missed
    // event or a reconnect gap. Returns false once nothing is processing, so
    // there's zero idle polling.
    refetchInterval: (query) => {
      const data = query.state.data as any[] | undefined;
      const stillProcessing = Array.isArray(data) && data.some((l) => l?.publishStatus === 'processing');
      return stillProcessing ? 60000 : false;
    },
  });
  const { mutateAsync: createMapAsync, isPending: createMapLoading } = useMutation(createMap());
  const { mutateAsync: editMapAsync, isPending: editMapLoading } = useMutation(editMap());
  const { mutateAsync: getLayerBoundsAsync } = useMutation(getLayerBounds());
  const queryClient = useQueryClient();
  const { setState: setMapContextState } = useMapContext();
  const layerPropsContext = useLayerProperitesContext();
  const sketchLogic = useSketchLogic(viewerRef, String(expandedCard));
  const {
    setOpen,
    setSketchDrawerVisible,
    setAttributeModalVisible,
    setModalVisible,
    setDrawFeatureModalVisible,
    setQueryToolModalVisible,
    setQueryResultModalVisible,
    setProximityToolModalVisible,
    setLayerUploadFlow,
  } = useLegendUI();
  const resetLayerPropertiesContext = layerPropsContext.setState;

  // ... (mapSettings state, other states, useEffects, handlers remain unchanged) ...
  const [mapSettings, setMapSettings] = useState({
    general: {
      mapTitle: '',
      mapDescription: '',
      thumbnailImage: '',
      selectedColor: '#333333',
      selectedBasemap: false,
      selectedMapPosition: '',
      defaultMapPosition: { type: '', coordinates: {} as { lat?: number; lng?: number; zoom?: number } },
      mapLogo: '',
    },
    permissions: {
      isLive: true,
      users: [
        { name: 'JACK_SMITH', checked: true },
        { name: 'JANE_DOE', checked: false },
        { name: 'JOHN_TUCKER', checked: false },
        { name: 'JANE_NEWUSER', checked: true },
      ],
      groups: [{ name: 'PUBLIC WORKS', checked: false }],
      password: '',
    },
    toolbox: {
      proximityTool: { enabled: false, name: 'Proximity Tool', color: '#1677ff' },
      queryTool: { enabled: false, name: 'Query Tool', color: '#1677ff' },
    },
  });

  // The header tracks the last saved logo, not the one picked in Map Settings.
  const [savedLogo, setSavedLogo] = useState<string | File>('');
  const [logoObjectUrl, setLogoObjectUrl] = useState('');
  const [drawerItems, setDrawerItems] = useState<any>([]);
  // Group checkbox = map visibility of the whole group, independent of each
  // layer's own displayOnMap (a layer renders only when both are on).
  const [hiddenGroupIds, setHiddenGroupIds] = useState<number[]>([]);
  const [boundingLayers, setBoundingLayers] = useState<any>({});
  const [drawingMode, setDrawingMode] = useState('point');
  const [selectedColumns, setSelectedColumns] = useState<any>({});
  const [currentTool, setCurrentTool] = useState<any>('');
  const [mapScreenshot, setMapScreenshot] = useState('');
  const [isLayerLoading, setIsLayerLoading] = useState(false);
  const [savedToolboxSettings, setSavedToolboxSettings] = useState({
    queryTool: { enabled: false, name: 'Query Tool', color: '#1677ff' },
    proximityTool: { enabled: false, name: 'Proximity Tool', color: '#1677ff' },
  });

  // Fetch Map Data
  useEffect(() => {
    if (!expandedCard || expandedCard === 'new-map' || !map?.map) return;
    const savedPosition = map.map.general?.defaultMapPosition;
    const coordinates = savedPosition?.coordinates || {};
    // Restoring the type keeps the next save from downgrading a saved view to full extent.
    const positionType = savedPosition?.type ?? (coordinates.lat !== undefined ? 'current_map_view' : '');

    setMapSettings((prevSettings) => ({
      ...prevSettings,
      ...map.map,
      general: {
        ...prevSettings.general,
        ...map.map.general,
        selectedMapPosition: positionType,
        defaultMapPosition: { type: positionType, coordinates },
      },
    }));

    setSavedLogo(map.map.general?.mapLogo ?? '');
    setMapContextState((prev: any) => ({ ...prev, id: Number(expandedCard) }));

    setSelectedColumns({
      querySelectedColumns: map?.selectedColumns?.query_selected_columns,
      proximitySelectedColumns: map?.selectedColumns?.proximity_selected_columns,
    });

    setSavedToolboxSettings(map.map.toolbox);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [map, expandedCard]);

  useEffect(() => {
    const resetUI = () => {
      setOpen(false);
      setSketchDrawerVisible(false);
      setAttributeModalVisible(false);
      setModalVisible(false);
      setLayerUploadFlow(null);
      setDrawFeatureModalVisible(false);
      setQueryToolModalVisible(false);
      setQueryResultModalVisible(false);
      setProximityToolModalVisible(false);
      sketchLogic.setDrawingMode('');
      // Clear this map's bounds and position so the next map doesn't open on them
      // while its own settings are still loading.
      setBoundingLayers({});
      setMapSettings((prev) => ({
        ...prev,
        general: { ...prev.general, selectedMapPosition: '', defaultMapPosition: { type: '', coordinates: {} } },
      }));

      resetLayerPropertiesContext((prev) => ({
        ...prev,
        selectedFeatureIds: [],
        selectionCql: null,
        highlightColor: null,
      }));
    };

    return () => {
      resetUI();
    };
  }, [
    expandedCard,
    setOpen,
    setSketchDrawerVisible,
    setAttributeModalVisible,
    setModalVisible,
    setLayerUploadFlow,
    setDrawFeatureModalVisible,
    setQueryToolModalVisible,
    setQueryResultModalVisible,
    setProximityToolModalVisible,
    sketchLogic.setDrawingMode,
    resetLayerPropertiesContext,
  ]);

  // Live layer-publish status. The backend emits `layer:status` to the map room
  // when an ingest job lands, so the legend flips "Processing…" → ready without
  // waiting for the backstop refetch above.
  useEffect(() => {
    if (!mapIdNumber) return;
    const leaveRoom = subscribeRoom('map:subscribe', { mapId: mapIdNumber }, 'map:unsubscribe');
    const off = onSocketEvent('layer:status', () => {
      void refetchMapLayers();
    });
    return () => {
      off();
      leaveRoom();
    };
  }, [mapIdNumber, refetchMapLayers]);

  // Fetch Layers. Only rebuild drawerItems when the SERVER data actually
  // changed. The backstop poll refetches while a layer publishes (as does the
  // socket event above), and axios hands back a fresh array each time — without this guard every
  // identical poll would reset drawerItems, clobbering an in-progress reorder
  // and re-rendering the map. (Equal strings are Object.is-equal, so the effect
  // skips when nothing meaningful changed.)
  const layersServerSig = useMemo(
    () =>
      Array.isArray(mapLayersResponse)
        ? JSON.stringify(mapLayersResponse.map((l: any) => [l.id, l.publishStatus, l.sortOrder, l.displayOnMap, l.displayInLegend, l.groupId]))
        : '',
    [mapLayersResponse],
  );
  useEffect(() => {
    if (!mapLayersResponse) return;
    const mapLayerItems = mapLayersResponse.map((item: any) => ({
      ...item,
      toggled: true,
    }));
    setDrawerItems(mapLayerItems);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [layersServerSig]);

  // Auto-zoom after upload: a layer that was "processing" on the previous fetch
  // and is now "ready" just finished publishing — fly the camera to it so the
  // user lands on their upload with no manual action. Keyed on the
  // processing→ready transition, so it never fires for pre-existing layers.
  const prevProcessingIdsRef = useRef<Set<number>>(new Set());
  const autoZoomToLayer = useCallback(
    async (layerName: string) => {
      try {
        const bounds: any = await getLayerBoundsAsync({ layerName });
        const nums = [bounds?.minX ?? bounds?.minx, bounds?.minY ?? bounds?.miny, bounds?.maxX ?? bounds?.maxx, bounds?.maxY ?? bounds?.maxy].map(
          (v: any) => parseFloat(v),
        );
        const isEmpty = !bounds || nums.some((n) => Number.isNaN(n)) || nums.every((n) => n === 0);
        if (!isEmpty) setBoundingLayers(bounds);
      } catch {
        /* zoom is best-effort — never block on it */
      }
    },
    [getLayerBoundsAsync],
  );
  useEffect(() => {
    if (!Array.isArray(mapLayersResponse)) return;
    const layers = mapLayersResponse as any[];
    const nowProcessing = new Set<number>(layers.filter((l) => l?.publishStatus === 'processing').map((l) => l.id));
    const justReady = layers.filter((l) => l?.publishStatus === 'ready' && prevProcessingIdsRef.current.has(l.id));
    prevProcessingIdsRef.current = nowProcessing;
    if (justReady.length) {
      const target = justReady.reduce((a, b) => (b.id > a.id ? b : a));
      void autoZoomToLayer(target.uniqueLayerName);
    }
  }, [mapLayersResponse, autoZoomToLayer]);

  // US-101662 Scenario 7: live-preview the in-progress symbology on the map while Layer Properties is open.
  const [symbologyPreview, setSymbologyPreview] = useState<SymbologyPreview | null>(null);
  useSymbologyPreview({ layers: drawerItems, setPreview: setSymbologyPreview });
  // `never` matches MapPortalMap's `layers` prop type.
  const previewedLayers = useMemo(() => applySymbologyPreview<never>(drawerItems, symbologyPreview), [drawerItems, symbologyPreview]);

  const handleGroupVisibilityChange = useCallback((groupId: number, visible: boolean) => {
    setHiddenGroupIds((prev) => (visible ? prev.filter((id) => id !== groupId) : prev.includes(groupId) ? prev : [...prev, groupId]));
  }, []);

  const liveLayers = useMemo(() => {
    if (!hiddenGroupIds.length) return previewedLayers;
    return previewedLayers.map((l: any) => (l.groupId != null && hiddenGroupIds.includes(l.groupId) ? { ...l, displayOnMap: false } : l)) as never[];
  }, [previewedLayers, hiddenGroupIds]);

  // Apply Theme Color
  useEffect(() => {
    const contentElement = document.querySelector('.ant-layout-content');
    if (contentElement) {
      (contentElement as HTMLElement).style.backgroundColor = mapSettings?.general?.selectedColor;
    }
  }, [mapSettings?.general?.selectedColor]);

  // --- HANDLERS ---

  const showModal = (modalType: keyof ModalVisibility) => setIsModalVisible({ ...isModalVisible, [modalType]: true });

  const handleCancel = (modalType: keyof ModalVisibility) => setIsModalVisible((prev) => ({ ...prev, [modalType]: false }));

  const updateMapSettings = (section: string, key: string, value: any) => {
    setMapSettings((prevSettings: any) => {
      const sectionClone = { ...prevSettings[section] };
      const keys = key.split('.');
      let current = sectionClone;

      for (let i = 0; i < keys.length - 1; i++) {
        const k = keys[i];
        current[k] = current[k] || {};
        current = current[k];
      }
      current[keys[keys.length - 1]] = value;

      return {
        ...prevSettings,
        [section]: sectionClone,
      };
    });
  };

  const handleSettingsCancel = () => {
    updateMapSettings('general', 'mapLogo', savedLogo);
    handleCancel(isNewMap ? 'newMap' : 'settings');
  };

  const handleLayerUpdateComplete = useCallback(async () => {
    if (!expandedCard) return;
    try {
      setIsLayerLoading(true);
      const response = await refetchMapLayers();
      const mapLayers = response.data;
      if (mapLayers) {
        const mapLayerItems = mapLayers.map((item: any) => ({ ...item, toggled: true }));
        setDrawerItems(mapLayerItems);
      }
    } catch (error) {
      console.error('Error refreshing layers:', error);
    } finally {
      setIsLayerLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [expandedCard, refetchMapLayers]);

  // --- SCREENSHOT LOGIC ---
  const preserveWebGLContext = useCallback((element: any) => {
    const canvases = element.getElementsByTagName('canvas');
    const webglContexts: any = [];
    Array.from(canvases).forEach((canvas: any) => {
      const context = canvas.getContext('webgl') || canvas.getContext('webgl2');
      if (context) {
        webglContexts.push({
          canvas,
          context,
          preserveDrawingBuffer: context.getContextAttributes().preserveDrawingBuffer,
        });
        const attributes = { preserveDrawingBuffer: true, antialias: true };
        canvas.getContext('webgl', attributes) || canvas.getContext('webgl2', attributes);
      }
    });
    return webglContexts;
  }, []);

  const restoreWebGLContext = useCallback((webglContexts: any) => {
    webglContexts.forEach(({ canvas, preserveDrawingBuffer }: any) => {
      const attributes = { preserveDrawingBuffer, antialias: true };
      canvas.getContext('webgl', attributes) || canvas.getContext('webgl2', attributes);
    });
  }, []);

  const captureComponent = useCallback(
    async (componentRef: any) => {
      try {
        if (!componentRef) {
          console.error('Component reference not found');
          return;
        }
        const webglContexts = preserveWebGLContext(componentRef);
        await new Promise((resolve) => requestAnimationFrame(resolve));
        const targetWidth = 1300;
        const targetHeight = 600;

        const canvas = await html2canvas(componentRef, {
          useCORS: true,
          allowTaint: true,
          logging: false,
          scale: 0.5,
          backgroundColor: null,
          removeContainer: true,
          foreignObjectRendering: false,
          width: targetWidth,
          height: targetHeight,
          onclone: (clonedDoc) => {
            const clonedElement = clonedDoc.getElementById(componentRef.id);
            if (clonedElement) preserveWebGLContext(clonedElement);
          },
        });

        setMapScreenshot(canvas.toDataURL('image/png'));
        restoreWebGLContext(webglContexts);
      } catch (error) {
        console.error('Error capturing screenshot:', error);
      }
    },
    [preserveWebGLContext, restoreWebGLContext],
  );

  const handleCurrentMapView = useCallback(() => {
    const mapComponent = document.getElementsByClassName('mapportal-map-container')[0];
    if (mapComponent) {
      setTimeout(() => captureComponent(mapComponent), 1000);
    }
  }, [captureComponent]);

  const base64ToBlob = (base64String: string) => {
    const parts = base64String.split(';base64,');
    const contentType = parts[0].split(':')[1];
    const raw = window.atob(parts[1]);
    const rawLength = raw.length;
    const uInt8Array = new Uint8Array(rawLength);
    for (let i = 0; i < rawLength; ++i) {
      uInt8Array[i] = raw.charCodeAt(i);
    }
    return new Blob([uInt8Array], { type: contentType });
  };

  const handleMapPositionClick = (position: string) => {
    if (!viewerRef.current) return;
    const viewer = viewerRef.current;

    if (position === 'current_map_view') {
      const coordinates = {
        lat: Cesium.Math.toDegrees(viewer.camera.positionCartographic.latitude),
        lng: Cesium.Math.toDegrees(viewer.camera.positionCartographic.longitude),
        zoom: viewer.camera.positionCartographic.height,
      };
      setMapSettings((prevSettings: any) => ({
        ...prevSettings,
        general: {
          ...prevSettings.general,
          selectedMapPosition: position,
          defaultMapPosition: { type: position, coordinates },
        },
      }));
    }
  };

  const handleSave = async () => {
    let generalFormData: { mapTitle: string; mapDescription: string } | null | undefined;
    if (generalTabRef.current) {
      generalFormData = generalTabRef.current.submit();
      // submit() returns null when map title validation fails.
      if (generalFormData === null) {
        return;
      }
    }

    setIsLayerLoading(true);
    try {
      // Close whichever modal was open
      handleCancel(isNewMap ? 'newMap' : 'settings');

      if (generalFormData) {
        Object.entries(generalFormData).forEach(([key, value]) => {
          updateMapSettings('general', key, value);
        });
      }

      let coordinatesToSave = { ...mapSettings.general.defaultMapPosition.coordinates };
      const positionType = mapSettings.general.selectedMapPosition === 'current_map_view' ? 'current_map_view' : 'full_extent';

      if (positionType === 'current_map_view' && viewerRef.current) {
        const viewer = viewerRef.current;
        coordinatesToSave = {
          lat: Cesium.Math.toDegrees(viewer.camera.positionCartographic.latitude),
          lng: Cesium.Math.toDegrees(viewer.camera.positionCartographic.longitude),
          zoom: viewer.camera.positionCartographic.height,
        };
      } else if (positionType === 'full_extent') {
        coordinatesToSave = { lat: 0, lng: 0, zoom: 25000000 };
      }

      const saveData = {
        general: {
          map_title: generalFormData?.mapTitle ?? mapSettings.general.mapTitle,
          map_description: generalFormData?.mapDescription ?? mapSettings.general.mapDescription,
          map_theme: mapSettings.general.selectedColor,
          default_map_position: {
            type: positionType,
            coordinates: coordinatesToSave,
          },
          default_basemap: mapSettings.general.selectedBasemap ? 'streets-v11' : 'default-basemap',
        },
        permissions: {
          status: mapSettings?.permissions?.isLive ? 'ONLINE' : 'OFFLINE',
          visibility: 'PUBLIC',
          password: mapSettings?.permissions?.password,
          allowed_groups: mapSettings?.permissions?.groups.filter((g: any) => g?.checked).map((g: any) => g?.name),
          allowed_users: mapSettings?.permissions?.users.filter((u: any) => u?.checked).map((u: any) => u?.name),
          can_delete: true,
        },
        toolbox: {
          tools: [
            {
              tool_name: 'query_tool',
              display_name: mapSettings?.toolbox?.queryTool?.name,
              is_enabled: mapSettings?.toolbox?.queryTool?.enabled,
              highlight_color: mapSettings?.toolbox?.queryTool?.color,
              show_results: true,
              settings: { layers: [{ name: 'layer_name', enabled: true, fields: ['field1', 'field2'] }] },
            },
            {
              tool_name: 'proximity_tool',
              display_name: mapSettings?.toolbox?.proximityTool?.name,
              is_enabled: mapSettings?.toolbox?.proximityTool?.enabled,
              highlight_color: mapSettings?.toolbox?.proximityTool?.color,
              show_results: false,
              settings: { layers: [{ name: 'layer_name', enabled: true, fields: ['field1', 'field2'] }] },
            },
          ],
          sync_status: { sync_to_query_tool: true, sync_to_proximity_tool: false },
        },
      };

      const formDataToSend = new FormData();
      formDataToSend.append('settings', JSON.stringify(saveData));

      if (mapScreenshot || typeof mapSettings?.general?.thumbnailImage === 'object') {
        formDataToSend.append('file', mapSettings?.general?.thumbnailImage || (mapScreenshot && base64ToBlob(mapScreenshot)));
      }
      if (mapSettings?.general?.mapLogo && typeof mapSettings?.general?.mapLogo === 'object') {
        formDataToSend.append('mapLogo', mapSettings?.general?.mapLogo);
      }

      if (isNewMap) {
        const response = await createMapAsync({
          mapData: formDataToSend,
        });
        if (response) {
          navigate(`/services/map-portal/${response.id}`, { replace: true });
          setSavedLogo(response.map_logo ? `${S3_BASE}${response.map_logo}` : mapSettings.general.mapLogo);
          setMapSettings((prev) => ({
            ...prev,
            ...response,
            general: {
              ...prev.general,
              ...(response.map_logo && { mapLogo: `${S3_BASE}${response.map_logo}` }),
            },
          }));
          queryClient.invalidateQueries({ queryKey: [QueryKeys.GET_ALL_MAPS] });
        }
      } else {
        const response = await editMapAsync({
          mapId: expandedCard!,
          mapData: formDataToSend,
        });
        if (response) {
          const updatedGeneral = {
            ...mapSettings.general,
            ...response.general,
            mapTitle: generalFormData?.mapTitle ?? mapSettings.general.mapTitle,
            mapDescription: generalFormData?.mapDescription ?? mapSettings.general.mapDescription,
            selectedMapPosition: positionType,
            defaultMapPosition: { type: positionType, coordinates: coordinatesToSave },
            ...(response.map_logo && { mapLogo: `${S3_BASE}${response.map_logo}` }),
          };
          setMapSettings({ ...mapSettings, ...response, general: updatedGeneral });
          // Otherwise the tools keep the old colour until the map refetches.
          if (mapSettings.toolbox) setSavedToolboxSettings(mapSettings.toolbox);
          setSavedLogo(updatedGeneral.mapLogo);
          // The 30s global staleTime would otherwise serve the pre-save map on the next visit.
          queryClient.invalidateQueries({ queryKey: [QueryKeys.GET_MAP_BY_ID, expandedCard] });
          queryClient.invalidateQueries({ queryKey: [QueryKeys.GET_ALL_MAPS] });
        }
      }
    } catch (error) {
      console.error('Error saving map:', error);
    } finally {
      setIsLayerLoading(false);
    }
  };

  const handlePrint = () => {
    if (expandedCard && viewerRef.current) {
      const viewer = viewerRef.current;
      const camera = viewer.camera;
      const c = camera.positionCartographic;
      const params = new URLSearchParams({
        lat: String(Cesium.Math.toDegrees(c.latitude)),
        lng: String(Cesium.Math.toDegrees(c.longitude)),
        z: String(c.height),
        h: String(camera.heading),
        p: String(camera.pitch),
        r: String(camera.roll),
      });
      window.open(`/services/map-portal/${expandedCard}/print?${params.toString()}`, '_blank');
    }
  };

  // A just-saved logo is still a File when the response omits its key.
  useEffect(() => {
    if (typeof savedLogo === 'string' || !savedLogo) {
      setLogoObjectUrl('');
      return;
    }
    const url = URL.createObjectURL(savedLogo);
    setLogoObjectUrl(url);
    return () => URL.revokeObjectURL(url);
  }, [savedLogo]);

  const logoSrc = logoObjectUrl || (typeof savedLogo === 'string' && !savedLogo.endsWith('/') ? savedLogo : '');

  return (
    <div className="expanded-map-view">
      <Spinner isLoading={mapLoading || mapLayersLoading || createMapLoading || editMapLoading} />

      {/* Header Info */}
      {(logoSrc || mapSettings?.general?.mapTitle || mapSettings?.general?.mapDescription) && (
        <div className="header-container">
          {logoSrc && <img src={logoSrc} alt="Map Logo" className="header-logo" />}
          <div className="header-texts">
            {mapSettings?.general?.mapTitle && <div className="header-title">{parse(mapSettings.general.mapTitle)}</div>}
            {mapSettings?.general?.mapDescription && <div className="header-description">{parse(mapSettings.general.mapDescription)}</div>}
          </div>
        </div>
      )}

      {/* Toolbar */}
      <div className="expanded-map-view__toolbar">
        <MapPortalToolbar onShowSettings={() => showModal('settings')} onPrint={handlePrint} />
      </div>

      {/* Main Map Card — fills remaining viewport; no page scroll */}
      <Card className="expanded-card" styles={{ body: { height: '100%', padding: 0 } }}>
        <div className="expanded-card__body">
          <CustomMapMenu
            drawingMode={drawingMode}
            setDrawingMode={setDrawingMode}
            drawerItems={drawerItems}
            setDrawerItems={setDrawerItems}
            setCurrentTool={setCurrentTool}
            settings={savedToolboxSettings}
            mapId={String(expandedCard)}
            viewerRef={viewerRef}
            setIsLayerLoading={setIsLayerLoading}
            onLayerUpdateComplete={handleLayerUpdateComplete}
          />

          <MapPortalMap
            boundingLayers={boundingLayers}
            containerId={expandedCard !== null ? expandedCard + 1 : ''}
            layers={liveLayers}
            drawingMode={drawingMode}
            currentTool={currentTool}
            setCurrentTool={setCurrentTool}
            viewerRef={viewerRef}
            defaultMapPositions={mapSettings.general}
            mapId={expandedCard ?? undefined}
            isLayerLoading={isLayerLoading}
          />

          <LegendDrawer
            setBoundingLayers={setBoundingLayers}
            folderId={layerPropsContext.folderId}
            drawerItems={drawerItems}
            setDrawerItems={setDrawerItems}
            hiddenGroupIds={hiddenGroupIds}
            onGroupVisibilityChange={handleGroupVisibilityChange}
            mapId={String(expandedCard)}
            viewerRef={viewerRef}
            onLayerUpdateComplete={handleLayerUpdateComplete}
            setIsLayerLoading={setIsLayerLoading}
          />

          <Button
            shape="circle"
            icon={<PlusOutlined className="plus-outlined" />}
            size="large"
            className="custom-float-button"
            onClick={() => {
              setIsDataPortalModalVisible(!isDataPortalModalVisible);
              resetLayerPropertiesContext({
                ...defaultLayerPropertiesState,
                setState: resetLayerPropertiesContext,
              });
            }}
          />
        </div>
      </Card>

      {/* Reusable Settings Modal */}
      {/* Used for both Editing Existing Map and Creating New Map (via isNewMap logic handled inside) */}
      <MapSettingsModal
        visible={isModalVisible.settings || isModalVisible.newMap}
        onCancel={handleSettingsCancel}
        onSave={handleSave}
        mapSettings={mapSettings}
        updateSettings={updateMapSettings}
        generalTabRef={generalTabRef}
        modalVisibility={isModalVisible}
        showSubModal={showModal}
        hideSubModal={handleCancel}
        mapScreenshot={mapScreenshot}
        onCapture={handleCurrentMapView}
        onMapPositionClick={handleMapPositionClick}
        mapId={`${id}`}
        drawerItems={drawerItems}
        selectedColumns={selectedColumns}
        setSelectedColumns={setSelectedColumns}
        isNewMap={isNewMap}
      />
    </div>
  );
};

export default ExpandedMapView;
